import type {
  AgentApproval,
  AgentMode,
  AgentPendingApprovalPayload,
  AgentThreadSummary,
  WorkingMemoryPatchRequest,
  WorkingMemoryPayload,
  WorkingMemoryV1,
} from '@tabbio/types';
import { derivePanelCommands } from './agent-panel-sync';
import { emitPanelCommands } from './panel-commands';
import { api } from './api';

/**
 * A refused approval decision, with the envelope code so the card can explain
 * it in the user's language instead of showing the server's English.
 */
export class AgentApprovalError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'AgentApprovalError';
  }
}

/**
 * B10: an approval bound to what it was for (an agent CV publish) is refused
 * once the CV changed or 10 minutes passed. The i18n key that says so, or
 * null for any other failure (the card then shows the server message).
 */
const APPROVAL_FAILURE_COPY_KEYS: Record<string, string> = {
  APPROVAL_STALE: 'chat.approval.stale',
  APPROVAL_EXPIRED: 'chat.approval.expired',
};

export function approvalFailureCopyKey(error: unknown): string | null {
  return error instanceof AgentApprovalError
    ? (APPROVAL_FAILURE_COPY_KEYS[error.code] ?? null)
    : null;
}

export class WorkingMemoryApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'WorkingMemoryApiError';
  }
}

function unwrapWorkingMemory(res: {
  data: WorkingMemoryPayload | null;
  error: { code: string; message: string } | null;
}) {
  if (res.error) {
    throw new WorkingMemoryApiError(res.error.message, res.error.code);
  }
  if (!res.data) {
    throw new WorkingMemoryApiError(
      'Working memory response was empty',
      'EMPTY_RESPONSE',
    );
  }
  return res.data.workingMemory;
}

export function getThreadRequestOptions(mode: AgentMode, companyId?: string) {
  return {
    headers: {
      'x-agent-mode': mode,
      ...(companyId ? { 'x-company-id': companyId } : {}),
    },
  };
}

export async function fetchThreads(mode: AgentMode, companyId?: string) {
  const params = new URLSearchParams({ mode });
  if (companyId) {
    params.set('companyId', companyId);
  }

  const url = `/api/agent/threads?${params.toString()}`;
  const res = await api.get<AgentThreadSummary[]>(url);
  return res.data ?? [];
}

export async function fetchThreadMessages(
  threadId: string,
  mode: AgentMode,
  companyId?: string,
): Promise<{
  messages: any[];
  toolCalls: any[];
  /** Tool calls still waiting on the user's approval, as the live stream sent them. */
  pendingApprovals: AgentPendingApprovalPayload[];
}> {
  const res = await api.get<any>(
    `/api/agent/threads/${threadId}/messages`,
    getThreadRequestOptions(mode, companyId),
  );
  if (res.error) throw new Error(res.error.message);
  const data = res.data;
  // Handle both new shape { messages, toolCalls } and legacy array
  if (data && Array.isArray(data.messages)) {
    return {
      messages: data.messages,
      toolCalls: data.toolCalls ?? [],
      pendingApprovals: Array.isArray(data.pendingApprovals) ? data.pendingApprovals : [],
    };
  }
  // Backwards compat: if the endpoint returned a plain array
  if (Array.isArray(data)) {
    return { messages: data, toolCalls: [], pendingApprovals: [] };
  }
  throw new Error('Conversation history response was empty or invalid');
}

/**
 * Rename a thread, or pin it to the top of the menu.
 *
 * The server-generated title is the first 80 characters of the user's opening
 * message, which is often a poor name for the conversation it became.
 */
export async function updateThread(
  threadId: string,
  patch: { title?: string; pinned?: boolean },
  mode: AgentMode,
  companyId?: string,
) {
  const res = await api.patch<AgentThreadSummary>(
    `/api/agent/threads/${threadId}`,
    patch,
    getThreadRequestOptions(mode, companyId),
  );
  if (res.error) throw new Error(res.error.message);
  return res.data;
}

export async function deleteThread(threadId: string, mode: AgentMode, companyId?: string) {
  const res = await api.delete<{ deleted: boolean }>(
    `/api/agent/threads/${threadId}`,
    getThreadRequestOptions(mode, companyId),
  );
  if (res.error) throw new Error(res.error.message);
  return res.data;
}

export async function confirmApproval(approvalId: string, reason?: string) {
  const res = await api.post<{
    approval: AgentApproval;
    assistant: {
      runId?: string;
      finishReason?: string;
      text?: string;
    };
  }>(`/api/agent/approvals/${approvalId}/confirm`, reason ? { reason } : undefined);

  if (res.error) throw new AgentApprovalError(res.error.message, res.error.code);
  if (!res.data) throw new Error('Approval confirmation returned no data');

  // Approval-gated tools (e.g. applicationSubmit) execute server-side and
  // never come back through the SSE tool-result path, so the content-panel
  // sync has to fire from here or the panel silently goes stale after the
  // very actions users care about most. On native nothing subscribes, so
  // this is a no-op there.
  try {
    const approval = res.data.approval;
    if (approval?.toolName) {
      emitPanelCommands(
        derivePanelCommands({
          toolName: approval.toolName,
          args: approval.args,
          status: 'completed',
        }),
      );
    }
  } catch {
    // Panel sync must never break the approval flow.
  }

  return res.data;
}

export async function rejectApproval(approvalId: string, reason?: string) {
  const res = await api.post<{
    approval: AgentApproval;
    assistant: {
      runId?: string;
      finishReason?: string;
      text?: string;
    };
  }>(`/api/agent/approvals/${approvalId}/reject`, reason ? { reason } : undefined);

  if (res.error) throw new AgentApprovalError(res.error.message, res.error.code);
  if (!res.data) throw new Error('Approval rejection returned no data');
  return res.data;
}

export async function fetchMemory() {
  const res = await api.get<WorkingMemoryPayload>('/api/agent/memory');
  return unwrapWorkingMemory(res);
}

export async function updateMemory(patch: Partial<WorkingMemoryV1>) {
  const body: WorkingMemoryPatchRequest = { patch };
  const res = await api.put<WorkingMemoryPayload>('/api/agent/memory', body);
  return unwrapWorkingMemory(res);
}

export async function clearMemory() {
  const res = await api.delete<WorkingMemoryPayload>('/api/agent/memory');
  return unwrapWorkingMemory(res);
}
